/**
 * Runner-side LambdaTest SmartUI provider for visual validation steps.
 *
 * Implements `IVisualTestingProvider` and mirrors the Extension SmartUI flow.
 * All capture types (viewport/element/full-page) are serialized as DOM snapshots
 * and uploaded via Testim Services, which proxies to SmartUI.
 *
 * Services proxy keeps credentials server-side and centralizes request handling.
 * DOM upload is used for all captures because SmartUI v2 screenshot upload does
 * not support per-upload comparison settings (for example, `matchLevel`).
 *
 * Terms mapping: `buildId`/`batchId` -> `buildName`, `screenshotName` ->
 * `snapshotName`, `ignoreDisplacements` -> `smartIgnore`, `matchLevel` ->
 * SmartUI comparison sensitivity.
 */
import { getLogger } from '../../commons/logger';
import * as testimServicesApi from '../../commons/testimServicesApi';
import type { IVisualTestingProvider, ScreenshotSettings, VisualValidationConfig, VisualTestingResult, MatchLevel, Region } from './IVisualTestingProvider';
import type { LambdaTestIntegrationData } from 'clickim/src/common/api/testimVisualTestingApi';

const logger = getLogger('lambdatest-provider');

interface SmartUIMobileConfig {
    devices: string[];
    fullPage: boolean;
    orientation: 'portrait' | 'landscape';
}

interface VisualGridConfig {
    browsers: string[];
    resolutions: Array<{ width: number; height: number }>;
    mobile?: SmartUIMobileConfig;
}

export interface LambdaTestConfig extends VisualValidationConfig<Partial<LambdaTestIntegrationData>> {
    testName: string;
    projectId: string;
    branch?: string;
    ignoreDisplacements?: boolean;
    visualGrid?: VisualGridConfig;
}

export interface LambdaTestResult extends VisualTestingResult {
    results?: unknown;
}

interface LambdaTestScreenshotSettings extends ScreenshotSettings {
    stepId?: string;
    visualGrid?: VisualGridConfig;
}

type CaptureType = 'screenshot' | 'dom';

interface CapturedData {
    type: CaptureType;
    screenshotName: string;
    smartIgnore: boolean;
    matchLevel?: MatchLevel;
    ignoreRegions?: Region[];
    screenshotBase64?: string;
    domContentBase64?: string;
    pageUrl?: string;
    browsers?: string[];
    viewports?: [number, number][];
    mobile?: SmartUIMobileConfig;
    fullPage?: boolean;
}

export class LambdaTestProvider implements IVisualTestingProvider {
    private driver: WebdriverIO.Browser | null = null;
    private config: LambdaTestConfig;
    private captures: CapturedData[] = [];
    private buildName: string;
    private branchName: string;
    private screenshotIndex = 0;

    constructor(config: LambdaTestConfig) {
        this.config = config;
        this.buildName = `Testim Run - ${config.testName} - ${config.buildId}`;
        const testimBranch = config.branch;
        this.branchName = !testimBranch || testimBranch === 'master' ? 'default' : testimBranch;
    }

    /** Stores the WebDriver instance for screenshot/DOM capture. Throws if driver is falsy. */
    initialize(driver: unknown): Promise<void> {
        if (!driver) {
            throw new Error('LambdaTest provider not initialized - driver is null');
        }
        this.driver = driver as WebdriverIO.Browser;
        return Promise.resolve();
    }

    /**
     * Captures a visual snapshot based on step settings. Called once per visual validation step.
     * Viewport/element captures produce a base64 PNG; full-page captures serialize the DOM.
     * Data is stored in memory and uploaded later in finalize().
     */
    async captureScreenshot(settings: ScreenshotSettings): Promise<void> {

        const ltSettings = settings as LambdaTestScreenshotSettings;
        this.screenshotIndex++;
        const action = ltSettings.region ? 'element' : (ltSettings.fully ? 'fullpage' : 'viewport');
        const screenshotName = ltSettings.stepId
            ? `${this.config.testName} - ${ltSettings.stepId} - ${action}`
            : `${this.config.testName} - ${action} ${this.screenshotIndex}`;

        const smartIgnore = !!(this.config.ignoreDisplacements || ltSettings.ignoreDisplacements);
        const matchLevel = ltSettings.matchLevel;
        const ignoreRegions = ltSettings.ignoreRegions;

        // All step types use DOM capture to support per-snapshot matchLevel comparison settings.
        // The v2 screenshot upload API does not support per-upload comparison settings.
        const isFullPage = !!ltSettings.fully;
        const capture = await this.captureDom(screenshotName, smartIgnore, matchLevel, ltSettings.visualGrid, ignoreRegions, isFullPage);
        this.captures.push(capture);
    }

    /**
     * Uploads all captures collected by captureScreenshot() to SmartUI via Services.
     * A new provider instance is created per visual validation step, so captures
     * are scoped to a single step and not shared across steps.
     * Upload failures are collected and returned (not retried) — the caller
     * decides whether to fail the test based on `success`.
     */
    async finalize(): Promise<LambdaTestResult> {
        if (this.captures.length === 0) {
            throw new Error('LambdaTest finalize called with no captures — captureScreenshot was never called');
        }

        const uploaded: string[] = [];
        const errors: { screenshotName: string; error: string }[] = [];
        let hasAnyChanges = false;
        let isAnyNewBaseline = false;
        let maxMismatchPercentage = 0;

        for (const capture of this.captures) {
            try {
                if (capture.type === 'dom') {
                    const result = await testimServicesApi.uploadLambdaTestDom({
                        projectId: this.config.projectId,
                        buildName: this.buildName,
                        snapshotName: capture.screenshotName,
                        domContent: capture.domContentBase64!,
                        url: capture.pageUrl,
                        branch: this.branchName,
                        browsers: capture.browsers,
                        viewports: capture.viewports,
                        mobile: capture.mobile,
                        matchLevel: capture.matchLevel,
                        smartIgnore: capture.smartIgnore || undefined,
                        ignoreRegions: capture.ignoreRegions,
                        fullPage: capture.fullPage,
                    });
                    uploaded.push(capture.screenshotName);
                    if (result.hasChanges) hasAnyChanges = true;
                    if (result.isNewBaseline) isAnyNewBaseline = true;
                    if (result.mismatchPercentage) {
                        maxMismatchPercentage = Math.max(maxMismatchPercentage, result.mismatchPercentage);
                    }
                } else {
                    const result = await testimServicesApi.uploadLambdaTestScreenshot({
                        projectId: this.config.projectId,
                        buildName: this.buildName,
                        screenshotName: capture.screenshotName,
                        screenshotData: capture.screenshotBase64!,
                        smartIgnore: capture.smartIgnore || undefined,
                    });
                    uploaded.push(capture.screenshotName);
                }
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                errors.push({ screenshotName: capture.screenshotName, error: message });
            }
        }

        if (errors.length > 0) {
            logger.error('LambdaTest visual testing finalized with failures', {
                uploaded: uploaded.length,
                failed: errors.length,
                buildName: this.buildName,
                errors,
            });
        }

        return {
            success: errors.length === 0,
            results: {
                uploaded: uploaded.length,
                failed: errors.length,
                buildName: this.buildName,
                screenshots: uploaded,
                errors: errors.length > 0 ? errors : undefined,
                hasChanges: hasAnyChanges,
                isNewBaseline: isAnyNewBaseline,
                mismatchPercentage: maxMismatchPercentage,
            },
            eyesResults: [{
                isNew: isAnyNewBaseline,
                editorUrl: `https://smartui.lambdatest.com/builds?buildName=${encodeURIComponent(this.buildName)}`,
            }],
        };
    }

    private async captureDom(
        screenshotName: string,
        smartIgnore: boolean,
        matchLevel?: MatchLevel,
        visualGrid?: VisualGridConfig,
        ignoreRegions?: Region[],
        fullPage = true,
    ): Promise<CapturedData> {
        const [domHtml, pageUrl] = await Promise.all([
            this.driver!.execute(() => document.documentElement.outerHTML),
            this.driver!.execute(() => document.URL),
        ]);

        const domContentBase64 = Buffer.from(domHtml, 'utf8').toString('base64');

        const gridConfig = visualGrid || this.config.visualGrid;
        const browsers = gridConfig?.browsers;
        const viewports = gridConfig?.resolutions.map(
            (r): [number, number] => [r.width, r.height],
        );
        const mobile = gridConfig?.mobile;

        return {
            type: 'dom',
            screenshotName,
            smartIgnore,
            matchLevel,
            ignoreRegions: ignoreRegions?.length ? ignoreRegions : undefined,
            domContentBase64,
            pageUrl,
            mobile,
            browsers,
            viewports,
            fullPage,
        };
    }
}
